/**
 * @fileoverview Route Definitions
 * @description Centralized route configuration for the application
 * @module @xtream/frontend/router/routes
 *
 * This file defines:
 * - All application routes
 * - Route paths as constants
 * - Route metadata (title, requiresAuth, allowedRoles)
 */

import { UserRole } from '@xtream/shared-types';

// ═══════════════════════════════════════════════════════════════════════════
// ROUTE PATHS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * All application routes as constants for type-safe navigation
 */
export const ROUTES = {
  // Auth routes (public)
  LOGIN: '/login',

  // Main routes (protected)
  DASHBOARD: '/dashboard',

  // Services routes
  SERVICES: '/services',
  SERVICE_NEW: '/services/new',
  SERVICE_DETAIL: '/services/:id',
  SERVICE_EDIT: '/services/:id/edit',

  // Profile
  PROFILE: '/profile',

  // Users routes (admin only)
  USERS: '/users',
  USER_NEW: '/users/new',
  USER_DETAIL: '/users/:id',
  USER_EDIT: '/users/:id/edit',

  // Settings
  SETTINGS: '/settings',

  // Error pages
  NOT_FOUND: '*',
} as const;

// ═══════════════════════════════════════════════════════════════════════════
// ROUTE METADATA
// ═══════════════════════════════════════════════════════════════════════════

export interface RouteConfig {
  path: string;
  title: string;
  requiresAuth: boolean;
  allowedRoles?: UserRole[];
  showInNav?: boolean;
  icon?: string;
}

/**
 * Route configuration with metadata
 */
export const routeConfigs: Record<keyof typeof ROUTES, RouteConfig> = {
  LOGIN: {
    path: ROUTES.LOGIN,
    title: 'Iniciar Sesión',
    requiresAuth: false,
    showInNav: false,
  },
  DASHBOARD: {
    path: ROUTES.DASHBOARD,
    title: 'Dashboard',
    requiresAuth: true,
    showInNav: true,
    icon: 'dashboard',
  },
  SERVICES: {
    path: ROUTES.SERVICES,
    title: 'Servicios',
    requiresAuth: true,
    showInNav: true,
    icon: 'services',
  },
  SERVICE_NEW: {
    path: ROUTES.SERVICE_NEW,
    title: 'Nuevo Servicio',
    requiresAuth: true,
    allowedRoles: [UserRole.ADMIN, UserRole.OPERATOR],
    showInNav: false,
  },
  SERVICE_DETAIL: {
    path: ROUTES.SERVICE_DETAIL,
    title: 'Detalle del Servicio',
    requiresAuth: true,
    showInNav: false,
  },
  SERVICE_EDIT: {
    path: ROUTES.SERVICE_EDIT,
    title: 'Editar Servicio',
    requiresAuth: true,
    allowedRoles: [UserRole.ADMIN, UserRole.OPERATOR],
    showInNav: false,
  },
  PROFILE: {
    path: ROUTES.PROFILE,
    title: 'Mi Perfil',
    requiresAuth: true,
    showInNav: false,
  },
  USERS: {
    path: ROUTES.USERS,
    title: 'Usuarios',
    requiresAuth: true,
    allowedRoles: [UserRole.ADMIN],
    showInNav: true,
    icon: 'users',
  },
  USER_NEW: {
    path: ROUTES.USER_NEW,
    title: 'Nuevo Usuario',
    requiresAuth: true,
    allowedRoles: [UserRole.ADMIN],
    showInNav: false,
  },
  USER_DETAIL: {
    path: ROUTES.USER_DETAIL,
    title: 'Detalle del Usuario',
    requiresAuth: true,
    allowedRoles: [UserRole.ADMIN],
    showInNav: false,
  },
  USER_EDIT: {
    path: ROUTES.USER_EDIT,
    title: 'Editar Usuario',
    requiresAuth: true,
    allowedRoles: [UserRole.ADMIN],
    showInNav: false,
  },
  SETTINGS: {
    path: ROUTES.SETTINGS,
    title: 'Configuración',
    requiresAuth: true,
    allowedRoles: [UserRole.ADMIN],
    showInNav: true,
    icon: 'settings',
  },
  NOT_FOUND: {
    path: ROUTES.NOT_FOUND,
    title: 'Página no encontrada',
    requiresAuth: false,
    showInNav: false,
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Get navigation items for sidebar
 * @param userRole - Current user's role
 * @returns Array of routes to show in navigation
 */
export function getNavigationItems(userRole?: UserRole): RouteConfig[] {
  return Object.values(routeConfigs).filter((route) => {
    if (!route.showInNav) return false;
    if (!route.allowedRoles) return true;
    if (!userRole) return false;
    return route.allowedRoles.includes(userRole);
  });
}

/**
 * Build a route path with parameters
 * @param route - Route path with params (e.g., '/services/:id')
 * @param params - Object with param values
 * @returns Built path
 *
 * @example
 * buildPath('/services/:id', { id: '123' }) // '/services/123'
 */
export function buildPath(route: string, params: Record<string, string>): string {
  let path = route;
  Object.entries(params).forEach(([key, value]) => {
    path = path.replace(`:${key}`, value);
  });
  return path;
}

/**
 * Check if a user has access to a route
 * @param route - Route configuration
 * @param userRole - Current user's role
 * @returns Whether user can access the route
 */
export function canAccessRoute(route: RouteConfig, userRole?: UserRole): boolean {
  if (!route.requiresAuth) return true;
  if (!userRole) return false;
  if (!route.allowedRoles) return true;
  return route.allowedRoles.includes(userRole);
}
